import { useMemo } from 'react';
import { Item } from '@/api/items/items.types';
import {
  CatalogFilters,
  SortDirection,
  SortField,
  useCatalogFilterStore,
} from '@/stores/catalog-filter-store';
import { compareByItemGroupThenName } from '@/utils/item-catalog-sort';

function filterAndSortItems(
  items: Item[],
  filters: CatalogFilters,
  searchText: string,
  sortField: SortField,
  sortDirection: SortDirection,
): Item[] {
  let result = [...items];

  if (filters.manufacturerId) {
    result = result.filter((i) => i.manufacturerId === filters.manufacturerId);
  }
  if (filters.itemGroupName) {
    result = result.filter((i) => i.itemGroup === filters.itemGroupName);
  }
  if (filters.onlyInStock) {
    result = result.filter(
      (i) => i.quantityOnStock - i.quantityOrderedByCustomers > 0,
    );
  }

  if (searchText.trim()) {
    const terms = searchText.toLowerCase().split(/\s+/);
    result = result.filter((item) => {
      const haystack =
        `${item.id} ${item.name} ${item.barCode ?? ''} ${item.manufacturer}`.toLowerCase();
      return terms.every((t) => haystack.includes(t));
    });
  }

  const dir = sortDirection === 'asc' ? 1 : -1;

  result.sort((a, b) => {
    switch (sortField) {
      case 'name':
        return compareByItemGroupThenName(a, b) * dir;
      case 'price': {
        if (a.itemPrice === 0 && b.itemPrice > 0) return 1;
        if (a.itemPrice > 0 && b.itemPrice === 0) return -1;
        return (a.itemPrice - b.itemPrice) * dir;
      }
      case 'stock': {
        const aStock = Math.max(
          a.quantityOnStock - a.quantityOrderedByCustomers,
          0,
        );
        const bStock = Math.max(
          b.quantityOnStock - b.quantityOrderedByCustomers,
          0,
        );
        return (aStock - bStock) * dir;
      }
      case 'articleNumber':
        return a.id.localeCompare(b.id) * dir;
      default:
        return 0;
    }
  });

  return result;
}

export function useFilteredItems(items: Item[]) {
  const { filters, searchText, sortField, sortDirection } =
    useCatalogFilterStore();

  return useMemo(
    () =>
      filterAndSortItems(items, filters, searchText, sortField, sortDirection),
    [items, filters, searchText, sortField, sortDirection],
  );
}
